import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Maximize, ArrowUpRight, Sparkles } from "lucide-react";
import { useLang } from "../../context/LanguageContext";
import { SmartImage } from "../SmartImage";
import { displayPrice, useUsdTry } from "../../lib/price";
import { badgeText } from "../../lib/badges";

const statusColor = (status) => {
  if (status === "Available") return "bg-green text-white";
  if (status === "Selling Fast") return "bg-gold text-charcoal";
  if (status === "Sold") return "bg-charcoal text-white";
  return "bg-charcoal/70 text-white";
};

export const ProjectCard = ({ project, index = 0 }) => {
  const { t, tc, lang } = useLang();
  const rate = useUsdTry();
  const price = displayPrice(project.price, project.price_try, lang, rate);
  const title = tc(project.title);
  const image = project.image_variants?.card?.url || project.image;
  const badge = badgeText(project.badge, lang);
  return (
    <Link
      to={`/projects/${project.slug}`}
      data-testid={`project-card-${project.slug}`}
      className="group block h-full"
    >
      <div className="relative overflow-hidden hover-zoom aspect-[4/3] bg-lightgray">
        {image ? <SmartImage src={image} variants={project.image_variants} sizes="(max-width: 640px) 50vw, 33vw" alt={title} fit="cover" className="w-full h-full" /> : <div className="w-full h-full flex items-center justify-center text-charcoal/30 text-sm">Image pending</div>}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
        <span className={`absolute top-2 start-2 sm:top-4 sm:start-4 text-[9px] sm:text-[11px] font-semibold tracking-wide px-2 py-1 sm:px-3 sm:py-1.5 rounded-full ${statusColor(project.status)}`}>
          {t.status[project.status] || project.status}
        </span>
        {badge && (
          <span data-testid={`project-badge-${project.slug}`}
            className="absolute top-2 end-2 sm:top-4 sm:end-4 max-w-[62%] inline-flex items-center gap-1 rounded-full bg-gold text-charcoal text-[9px] sm:text-[11px] font-bold tracking-wide px-2 py-1 sm:px-3 sm:py-1.5 shadow-lg shadow-charcoal/30 ring-2 ring-white/70 text-center leading-tight">
            <Sparkles className="w-3 h-3 shrink-0" /> <span className="line-clamp-2">{badge}</span>
          </span>
        )}
        <span className="hidden sm:flex absolute bottom-4 end-4 w-11 h-11 rounded-full glass items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-500 border border-white/30">
          <ArrowUpRight className="w-5 h-5 text-white" />
        </span>
      </div>
      <div className="px-2.5 sm:px-4 pt-3 pb-5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-charcoal/50 text-[10px] sm:text-xs mb-1.5">
          <span className="flex items-center gap-1 sm:gap-1.5"><MapPin className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gold" /> {project.location}</span>
          <span className="flex items-center gap-1 sm:gap-1.5"><Maximize className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-gold" /> {project.land_size}</span>
        </div>
        <h3 className="font-display text-base sm:text-2xl text-charcoal group-hover:text-green-light transition-colors leading-tight">{title}</h3>
        <p className="text-charcoal/55 text-[11px] sm:text-sm mt-1">{tc(project.project_type)}</p>
        {price && (
          <div className="mt-2 sm:mt-3 flex flex-col sm:flex-row sm:items-baseline sm:gap-2">
            <span className="text-[9px] sm:text-[11px] uppercase tracking-wider text-charcoal/55">{t.projects.price}</span>
            <span className="font-display text-sm sm:text-lg font-semibold text-green-deep">{price}</span>
          </div>
        )}
      </div>
    </Link>
  );
};
